import { CLASS_NAMES, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/* global FWP */ // Inform ESLint that FWP is a global variable

/**
 * A class for managing the Resource Sidebar
 */
export default class ResourceSidebar extends MessageBus {
  /**
   * Constructor function of the ResourceSidebar class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {object} element - DOM node that is passed into the constructor
     */
    this.element = element;
    this.init();
  }

  /**
   * Initializes the sidebar by caching DOM references, setting up event handlers,
   * and enabling event listeners.
   * @returns {object} ResourceSidebar - A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   *
   * Find and store all necessary DOM elements used in the sidebar.
   * @returns {object} ResourceSidebar - A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.isDesktop = window.innerWidth >= 1024;
    this.filterLists = this.element.querySelectorAll(
      `.${CLASS_NAMES.RESOURCE_FILTERS__FILTER_LIST}`
    );
    this.filterItems = this.element.querySelectorAll(
      `.${CLASS_NAMES.RESOURCE_FILTERS__FILTER_ITEM} > ${SELECTORS.H2}`
    );
    this.selectAllButtons = this.element.querySelectorAll(
      `.${CLASS_NAMES.SELECT_ALL}`
    );
    this.clearAllButtons = this.element.querySelectorAll(
      `.${CLASS_NAMES.CLEAR_ALL}`
    );
    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} ResourceSidebar - A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.onFilterListClick = this.onFilterListClick.bind(this);
    this.onFilterItemClick = this.onFilterItemClick.bind(this);
    this.onSelectAllClick = this.onSelectAllClick.bind(this);
    this.onClearAllClick = this.onClearAllClick.bind(this);
    return this;
  }

  /**
   * Enable event listeners and set up the view state based on screen size.
   * @returns {object} ResourceSidebar - A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (this.isDesktop) {
      document
        .querySelectorAll(`.${CLASS_NAMES.RESOURCE_FILTERS__MOBILE}`)
        .forEach((el) => el.remove());
    } else {
      document
        .querySelectorAll(`.${CLASS_NAMES.RESOURCE_FILTERS__DESKTOP}`)
        .forEach((el) => el.remove());
    }

    this.filterLists.forEach((list) =>
      list.addEventListener(EVENTS.CLICK, this.onFilterListClick)
    );
    this.filterItems.forEach((item) =>
      item.addEventListener(EVENTS.CLICK, this.onFilterItemClick)
    );
    this.selectAllButtons.forEach((button) =>
      button.addEventListener(EVENTS.CLICK, this.onSelectAllClick)
    );
    this.clearAllButtons.forEach((button) =>
      button.addEventListener(EVENTS.CLICK, this.onClearAllClick)
    );
    return this;
  }

  /**
   * Handles clicks on filter list headings, toggling visibility of sublists.
   * @param {Event} event - Click event
   * @returns {object} ResourceSidebar - A reference to the current instance of the class
   * @chainable
   */
  onFilterListClick(event) {
    const target = event.currentTarget;
    target.classList.toggle(`${CLASS_NAMES.MINUS}`);
    const nextElement = target.nextElementSibling;
    if (nextElement) {
      nextElement.classList.toggle(`${CLASS_NAMES.VISIBLE}`);
    }
    return this;
  }

  /**
   * Handles clicks on filter items, toggling expansion of nested lists.
   * @param {Event} event - Click event
   * @returns {object} ResourceSidebar - A reference to the current instance of the class
   * @chainable
   */
  onFilterItemClick(event) {
    const target = event.currentTarget;
    target.classList.toggle(`${CLASS_NAMES.MINUS}`);
    const siblingUl = target.nextElementSibling;
    if (siblingUl) {
      siblingUl.classList.toggle(`${CLASS_NAMES.EXPAND}`);
    }
    const siblingDiv = siblingUl ? siblingUl.nextElementSibling : null;
    if (siblingDiv) {
      siblingDiv.classList.toggle(`${CLASS_NAMES.EXPAND_LIST}`);
    }
    return this;
  }

  /**
   * Handles the 'Select All' button click, checking all unchecked filter checkboxes.
   * @param {Event} event - Click event
   * @returns {object} ResourceSidebar - A reference to the current instance of the class
   * @chainable
   */
  onSelectAllClick(event) {
    const parent = event.currentTarget.closest(
      `.${CLASS_NAMES.RESOURCE_FILTERS__QUICK_SELECT}`
    );
    if (!parent) return this;

    const facetContainer = parent.nextElementSibling;
    if (!facetContainer) return this;

    const checkboxes = facetContainer.querySelectorAll(
      `.${CLASS_NAMES.FACETWP_CHECKBOX}`
    );

    checkboxes.forEach((checkbox) => {
      if (!checkbox.classList.contains(`${CLASS_NAMES.CHECKED}`)) {
        checkbox.classList.add(`${CLASS_NAMES.CHECKED}`);
        const input = checkbox.querySelector('input[type="checkbox"]');
        if (input) {
          input.checked = true;
        }
      }
    });

    // Trigger FacetWP update
    if (typeof FWP !== 'undefined') {
      FWP.autoload();
    }

    return this;
  }

  /**
   * Handles the 'Clear All' button click, resetting the filter selection using FacetWP.
   * @param {Event} event - Click event
   * @returns {object} ResourceSidebar - A reference to the current instance of the class
   * @chainable
   */
  onClearAllClick(event) {
    if (typeof FWP !== 'undefined') {
      const parent = event.currentTarget.closest(
        `.${CLASS_NAMES.RESOURCE_FILTERS__QUICK_SELECT}`
      );
      if (!parent) return this;
      const facetName = parent.getAttribute(`${SELECTORS.DATA_FACET_NAME}`);
      if (facetName) {
        FWP.reset(facetName);
      }
    }
    return this;
  }
}
